import React, { useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { useProducts } from '../context/ProductsContext';
import './ProductDetail.css';

const INQUIRE_EMAIL = 'coryblossom22@gmail.com';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const ProductDetail = () => {
  const { slug } = useParams();
  const { findBySlug, products } = useProducts();
  const product = findBySlug(slug);

  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants.filter(Boolean) : []),
    [product]
  );
  const hasVariants = variants.length > 0;
  const [selectedVariantId, setSelectedVariantId] = useState(
    hasVariants ? variants[0].id : null
  );
  const selectedVariant = hasVariants
    ? variants.find((v) => v.id === selectedVariantId) || variants[0]
    : null;

  const gallery = useMemo(() => {
    if (!product) return [];
    if (hasVariants && selectedVariant?.image) {
      const base = product.gallery && product.gallery.length ? product.gallery : [product.img];
      return [selectedVariant.image, ...base.filter((g) => g !== selectedVariant.image)];
    }
    return product.gallery && product.gallery.length ? product.gallery : [product.img];
  }, [product, hasVariants, selectedVariant]);

  const [activeImage, setActiveImage] = useState(0);

  if (!product) return <Navigate to="/shop" replace />;

  const isWeddingInquiry = product.category === 'Weddings';

  const displayPrice = selectedVariant
    ? `$${Number(selectedVariant.price).toFixed(2)}`
    : product.price;

  const related = (products || [])
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  const inquirySubject = selectedVariant
    ? `Inquiry — ${product.name} (${selectedVariant.label})`
    : `Inquiry — ${product.name}`;
  const inquiryBody = selectedVariant
    ? `Hi Cory,\n\nI'm interested in the "${selectedVariant.label}" ${product.name}. Could you share availability and details?\n\nName:\nPhone:\nPickup/delivery preference:\nNotes:\n`
    : `Hi Cory,\n\nI'm interested in "${product.name}". Could you share availability and details?\n\nName:\nPhone:\nPickup/delivery preference:\nNotes:\n`;
  const mailtoHref = `mailto:${INQUIRE_EMAIL}?subject=${encodeURIComponent(inquirySubject)}&body=${encodeURIComponent(inquiryBody)}`;

  const selectVariant = (v) => {
    setSelectedVariantId(v.id);
    const idx = gallery.findIndex((g) => g === v.image);
    setActiveImage(idx >= 0 ? idx : 0);
  };

  return (
    <motion.main
      className="product-detail"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="product-detail__breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/shop">Shop</Link>
          <span>/</span>
          <span className="current">{product.name}</span>
        </div>
      </div>

      <section className="product-detail__hero">
        <div className="container">
          <div className="product-detail__grid">
            <AnimatedSection className="product-detail__images" direction="left">
              <motion.div
                className="product-detail__main-img"
                key={activeImage}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <img src={gallery[activeImage]} alt={product.name} />
              </motion.div>
              <div className="product-detail__thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    className={`product-detail__thumb ${activeImage === i ? 'product-detail__thumb--active' : ''}`}
                    onClick={() => setActiveImage(i)}
                  >
                    <img src={img} alt={`${product.name} view ${i + 1}`} />
                  </button>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection className="product-detail__info" direction="right">
              {product.badge && <span className="product-detail__badge">{product.badge}</span>}
              <span className="product-detail__category">{product.category}</span>
              <h1>{product.name}</h1>
              <p className="product-detail__price">
                {isWeddingInquiry
                  ? (product.priceNum > 0 ? `${product.price} — full quote after consult` : 'Custom quote — by consultation')
                  : displayPrice}
              </p>

              {selectedVariant && (
                <p className="product-detail__selected-label">
                  <span className="product-detail__selected-key">Selected</span>
                  <span className="product-detail__selected-value">{selectedVariant.label}</span>
                  {selectedVariant.sublabel && (
                    <span className="product-detail__selected-sublabel">{selectedVariant.sublabel}</span>
                  )}
                </p>
              )}

              {isWeddingInquiry && (
                <p className="product-detail__wedding-note">
                  Weddings are by consultation. Reach out to Cory with your date and vision — she&apos;ll reply to plan florals and pricing together.
                </p>
              )}

              <p className="product-detail__desc">{product.description}</p>

              {hasVariants && (
                <div className="product-detail__variants">
                  <p className="product-detail__variants-heading">
                    {product.details?.variantHeading || 'Choose an option'}
                  </p>
                  <div className="product-detail__variant-grid">
                    {variants.map((v) => {
                      const isSelected = v.id === selectedVariant?.id;
                      return (
                        <button
                          type="button"
                          key={v.id}
                          className={`product-detail__variant-card ${isSelected ? 'product-detail__variant-card--selected' : ''}`}
                          onClick={() => selectVariant(v)}
                        >
                          {v.image && (
                            <span className="product-detail__variant-card-img-wrap">
                              <img src={v.image} alt={v.label} loading="lazy" />
                            </span>
                          )}
                          <span className="product-detail__variant-card-label">{v.label}</span>
                          {v.sublabel && (
                            <span className="product-detail__variant-card-sublabel">{v.sublabel}</span>
                          )}
                          <span className="product-detail__variant-card-prices">
                            <span className="product-detail__variant-card-price">${Number(v.price).toFixed(2)}</span>
                            {typeof v.stock === 'number' && (
                              <span className="product-detail__variant-card-stock">
                                {v.stock > 0 ? 'Contact to inquire' : 'Sold out'}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hidden per client request — kept for easy restore.
              {product.flowers && product.flowers.length > 0 && (
                <div className="product-detail__flowers">
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    What's In This Arrangement
                  </h3>
                  <div className="flower-list">
                    {['Primary', 'Supporting'].map((type) => {
                      const group = product.flowers.filter((f) => f.type === type);
                      if (group.length === 0) return null;
                      return (
                        <div key={type} className="flower-group">
                          <span className="flower-group__label">{type} Blooms</span>
                          {group.map((flower, i) => (
                            <div key={i} className="flower-item">
                              <span className="flower-item__name">{flower.name}</span>
                              <span className="flower-item__detail">{flower.detail}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {product.details && Object.values(product.details).some(Boolean) && (
                <div className="product-detail__details">
                  <h3>Details</h3>
                  <table>
                    <tbody>
                      {product.details.bloomSeason && <tr><td>Bloom Season</td><td>{product.details.bloomSeason}</td></tr>}
                      {product.details.size && <tr><td>Size</td><td>{product.details.size}</td></tr>}
                      {product.details.vaseLife && <tr><td>Vase Life</td><td>{product.details.vaseLife}</td></tr>}
                      {product.details.includes && <tr><td>Includes</td><td>{product.details.includes}</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
              */}

              <div className="product-detail__actions">
                <a href={mailtoHref} className="btn btn-primary product-detail__order-btn">
                  {isWeddingInquiry ? 'Email Cory — Wedding Inquiry' : 'Inquire About This'}
                </a>
                <Link to="/contact" className="btn btn-outline">
                  Contact Form
                </Link>
              </div>

              {product.care && product.care.length > 0 && (
                <div className="product-detail__care">
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    Care Instructions
                  </h3>
                  <ul>
                    {product.care.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                </div>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="product-detail__region">
        <div className="container">
          <AnimatedSection className="region-banner">
            <div className="region-banner__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="region-banner__text">
              <h4>Grown in the Finger Lakes Region</h4>
              <p>All our flowers are sustainably grown on our farm in the heart of New York's beautiful Finger Lakes — known for its rolling hills, pristine lakes, and thriving agricultural community.</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {related.length > 0 && (
        <section className="product-detail__related">
          <div className="container">
            <AnimatedSection>
              <h2 className="section-title">You May Also Like</h2>
              <p className="section-subtitle">More from {product.category}</p>
            </AnimatedSection>
            <div className="product-detail__related-grid">
              {related.map((item, i) => (
                <AnimatedSection key={item.id} delay={i * 0.15}>
                  <Link to={`/shop/${item.slug}`} className="related-card">
                    <motion.div className="related-card__img" whileHover={{ scale: 1.03 }} transition={{ duration: 0.3 }}>
                      <img src={item.img} alt={item.name} loading="lazy" />
                    </motion.div>
                    <div className="related-card__info">
                      <h3>{item.name}</h3>
                      <span>{item.price}</span>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}
    </motion.main>
  );
};

export default ProductDetail;
